import { CAPTION_STYLE_DEFAULTS } from '@vbee-holding/aivoice-shared-lib';
import { clamp } from '@src/services/timeline';

// Cỡ chữ, độ dày viền và đổ bóng trong config đo ở hệ quy chiếu 1080, còn ô mẫu
// chỉ cao 80px nên thu nhỏ theo tỉ lệ này rồi chặn hai đầu cho thẻ khỏi vỡ.
const SAMPLE_SCALE = 0.5;
// Rộng hơn 1.35 của renderer để dấu tiếng Việt trên chữ IN HOA không bị cắt.
const LINE_HEIGHT = 1.5;
// Trần cỡ chữ và trần giãn nền dọc chọn sao cho tên dài nhất xuống hai dòng
// vẫn vừa ô cao 80px: 2 x 24 x 1.5 + 4 x 2 = 80.
const FONT_SIZE_MIN = 14;
const FONT_SIZE_MAX = 24;
// Độ giãn nền lên tới 1000% cỡ chữ, để nguyên là nền tràn khỏi thẻ.
const PADDING_X_MAX = 24;
const PADDING_Y_MAX = 4;

const TEXT_TRANSFORM = {
  UPPERCASE: 'uppercase',
  LOWERCASE: 'lowercase',
  CAPITALIZE: 'capitalize',
};

// Renderer tự vẽ hai gạch bằng fillRect với hằng số riêng. Dày hơn 0.06 của nó
// vì ô mẫu chặn cỡ chữ ở 24px. Vị trí gạch ngang thì CSS không đặt được.
const DECORATION_THICKNESS = 0.08;
const UNDERLINE_FROM_BASELINE = 0.08;

// Chip BOLD chỉ được làm đậm thêm: CSS bold là 700, gán thẳng sẽ kéo nhẹ đi
// những face vốn nặng hơn.
const BOLD_WEIGHT = 700;

// Độ trong suốt 0-100 của tài liệu thành hai ký tự alpha nối vào HEX.
const withOpacity = (hex, opacity) =>
  `${hex}${Math.round((opacity / 100) * 255)
    .toString(16)
    .padStart(2, '0')}`;

/**
 * Đổi config phụ đề của một style thành CSS cho dòng chữ mẫu trên thẻ: `block`
 * cho cả dòng, `glyph` cho riêng nét chữ.
 * Chỉ lấy phần nhận diện được bằng mắt — font, hoa thường, màu, viền, bóng,
 * nền. Vị trí, căn lề và bố cục khối không có nghĩa trong một ô cao 80px.
 */
const toSampleStyle = (config) => {
  if (!config?.text) return undefined;

  const text = { ...CAPTION_STYLE_DEFAULTS.text, ...config.text };
  const outline = { ...CAPTION_STYLE_DEFAULTS.outline, ...config.outline };
  const shadow = { ...CAPTION_STYLE_DEFAULTS.shadow, ...config.shadow };
  const background = {
    ...CAPTION_STYLE_DEFAULTS.background,
    ...config.background,
  };

  const fontSize = clamp(
    text.fontSize * SAMPLE_SCALE,
    FONT_SIZE_MIN,
    FONT_SIZE_MAX,
  );
  // Cỡ chữ còn qua bước chặn nên tỉ lệ thu nhỏ thật khác SAMPLE_SCALE. Viền và
  // bóng phải theo tỉ lệ này mới giữ đúng tương quan với chữ.
  const scale = fontSize / text.fontSize;
  const decorations = [
    text.formats.includes('UNDERLINE') && 'underline',
    text.formats.includes('STRIKETHROUGH') && 'line-through',
  ].filter(Boolean);

  const style = {
    // Tài liệu không đặt default cho font, thiếu thì để trình duyệt tự chọn.
    fontFamily: text.fontFamily
      ? `'${text.fontFamily}', sans-serif`
      : 'sans-serif',
    fontWeight: text.formats.includes('BOLD')
      ? Math.max(text.fontWeight, BOLD_WEIGHT)
      : text.fontWeight,
    fontStyle: text.formats.includes('ITALIC') ? 'italic' : 'normal',
    fontSize: `${fontSize}px`,
    lineHeight: LINE_HEIGHT,
    // Variant của CommonTypography kèm letter-spacing -0.4px; canvas thì không.
    letterSpacing: 'normal',
    color: withOpacity(text.color, text.opacity),
    textTransform: TEXT_TRANSFORM[text.textCase] || 'none',
    textDecoration: decorations.join(' ') || 'none',
    ...(decorations.length && {
      textDecorationThickness: `${DECORATION_THICKNESS}em`,
      textUnderlineOffset: `${UNDERLINE_FROM_BASELINE}em`,
    }),
  };

  // Viền để riêng: `-webkit-text-stroke` ăn cả hai đường gạch và nuốt chúng,
  // thành ra chúng mang màu viền. Renderer stroke ở khối riêng nên không dính.
  const glyph =
    outline.width > 0
      ? {
          WebkitTextStroke: `${outline.width * scale}px ${withOpacity(
            outline.color,
            outline.opacity,
          )}`,
          // Không có dòng này viền vẽ đè lên chữ và ăn mất nét.
          paintOrder: 'stroke fill',
        }
      : undefined;

  if (background.enabled) {
    const paddingX = Math.min(
      (background.paddingX / 100) * fontSize,
      PADDING_X_MAX,
    );
    const paddingY = Math.min(
      (background.paddingY / 100) * fontSize,
      PADDING_Y_MAX,
    );
    // Bo góc là % nửa cạnh ngắn hộp nền, cạnh ngắn ở đây là chiều cao.
    const boxHeight = fontSize * LINE_HEIGHT + paddingY * 2;
    style.padding = `${paddingY}px ${paddingX}px`;
    style['--sample-bg'] = withOpacity(background.color, background.opacity);
    style['--sample-bg-radius'] = `${
      (background.borderRadius / 100) * (boxHeight / 2)
    }px`;
    style['--sample-bg-blur'] = `${background.blur * scale}px`;
  }

  if (shadow.enabled) {
    const offsetX = shadow.offsetX * scale;
    const offsetY = shadow.offsetY * scale;
    const blur = shadow.blur * scale;
    const color = withOpacity(shadow.color, shadow.opacity);
    // Bóng của nét chữ, không phải của hộp nền — renderer cũng đổ lên fillText.
    style.textShadow = `${offsetX}px ${offsetY}px ${blur}px ${color}`;
  }

  return { block: style, glyph };
};

export default toSampleStyle;
